import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Typography,
} from '@mui/material';
import {ChevronRight} from '@vedmarket/ui-kit';
import {COLORS, ROUTES} from 'config/constants';
import {
  useNotification,
  useGetNotificationQuery,
  useReadNotificationsMutation,
} from 'api/notifications';
import {useGetUserMutation} from 'api/auth';

import NotificationBodyContent from '../notification-body-content';

import './details.scss';

const NotificationDetails = () => {
  const {t, i18n} = useTranslation();

  const {id} = useParams();
  const notificationId = id || '';
  const notification = useNotification();

  const {
    isFetching,
    isError,
    refetch,
  } = useGetNotificationQuery(notificationId, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const [ getUser, {isLoading: isUpdatingUser, isError: userError} ] = useGetUserMutation();

  const [
    readNotifications,
    {
      isLoading: isReading,
      isError: readError,
      isSuccess: isRead,
      reset: resetReadNotifications,
    },
  ] = useReadNotificationsMutation();

  const isNotificationLoading = isFetching || isReading;
  const isLoading = isNotificationLoading || isUpdatingUser;
  const hasNotificationError = isError || readError;
  const hasError = hasNotificationError || userError;
  const hasNotification = notification.id === Number(id);

  useEffect(() => {
    if (!hasNotification) return;

    const canMarkAsRead = notification.is_unread && !isFetching;
    const isActionBlocked = isReading || hasError;
    const shouldMarkAsRead = canMarkAsRead && !isActionBlocked;

    if (!shouldMarkAsRead) return;

    const ids = [ notification.id ];

    readNotifications({ids});
  }, [ notification, hasNotification, isFetching, isReading, hasError, readNotifications ]);

  useEffect(() => {
    if (isRead) {
      getUser({});
    }
  }, [ isRead, getUser ]);

  const handleRetry = () => {
    if (userError) {
      getUser({});
      return;
    }

    resetReadNotifications();
    refetch();
  };

  if (hasError) {
    return (
      <Box className='notification-details'>
        <Alert severity='error'>
          <Typography variant='body1'>{t('common__text__error')}</Typography>
        </Alert>
        <Button onClick={handleRetry}>
          <Typography variant='button'>{t('notifications__text__retry')}</Typography>
        </Button>
      </Box>
    );
  }

  if (isLoading || !hasNotification) {
    return <Box className='notification-details'><CircularProgress /></Box>;
  }

  return (
    <main className='notification-details' lang={i18n.language}>
      <header className='notification-details__header'>
        <Breadcrumbs separator={<ChevronRight width={20} height={20} style={{fill: COLORS.LIGHT_TEXT_DISABLED_OPACITY}} />}>
          <Link
            href={`/${ROUTES.CABINET}`}
            underline='hover'
            sx={{color: COLORS.LIGHT_TEXT_SECONDARY, cursor: 'pointer'}}
          >
            <Typography
              variant='body2'
              sx={{color: COLORS.LIGHT_TEXT_SECONDARY, lineHeight: '21px', letterSpacing: 0}}
            >
              {t('menu__text__main')}
            </Typography>
          </Link>
          <Link
            href={`/${ROUTES.CABINET}/${ROUTES.NOTIFICATIONS}`}
            underline='hover'
            sx={{color: COLORS.LIGHT_TEXT_SECONDARY, cursor: 'pointer'}}
          >
            <Typography
              variant='body2'
              sx={{color: COLORS.LIGHT_TEXT_SECONDARY, lineHeight: '21px', letterSpacing: 0}}
            >
              {t('notifications__text__title')}
            </Typography>
          </Link>
        </Breadcrumbs>
        <Typography
          component='h1'
          variant='h4'
          sx={{color: COLORS.BLACK_DARK, fontSize: 32, fontWeight: 700, lineHeight: '40px', letterSpacing: 0, hyphens: 'manual'}}
        >
          {notification.title}
        </Typography>
        <Box sx={{padding: '8px 16px 8px 0'}}>
          <Chip
            size='small'
            sx={{height: 24}}
            label={(
              <Typography
                variant='caption'
                sx={{color: COLORS.BLACK_DARK, fontSize: 12, lineHeight: '18px', letterSpacing: 0}}
              >
                {notification.section_display}
              </Typography>
            )}
          />
        </Box>
      </header>
      <Card component='article' className='notification-details__card' sx={{boxShadow: 'none', borderRadius: 0}}>
        <Typography
          variant='body2'
          sx={{color: COLORS.LIGHT_TEXT_DISABLED, lineHeight: '21px', letterSpacing: 0}}
        >
          {new Date(notification.created_at).toLocaleString('ru-RU', {timeZone: 'Europe/Moscow'})} МСК
        </Typography>
        <Divider sx={{margin: 0}} />
        <NotificationBodyContent
          body={notification.body}
          applicationId={notification.application_id}
          applicationUrl={notification.application_url}
        />
      </Card>
    </main>
  );
};

export default NotificationDetails;
